import React, { useEffect, useState } from 'react';
import { Branch } from './branch';
import { AppContext } from '../../App';
import { AdditionalRound } from './additional-round';
import { Arrow } from '../Svg/arrow';
import { GAtrack } from '../../Utils/tracking';

import './tree.scss';
import { getTrans } from '../../Utils/translate';

/**
 * sorts rounds by number
 *
 * @param {*} a
 * @param {*} b
 */
const roundSort = (a, b) => {
  if (!a.number || !b.number || a.number > b.number) return 1;
  if (a.number < b.number) return -1;
  return 0;
};

/**
 * finds last finished heat in list of all rounds
 *
 * @param {*} data
 */
const findActive = (data, heatId) => {
  const checkData = [...data].reverse();

  for (let roundI = 0; roundI < checkData.length; roundI++) {
    const checkRound = [...checkData[roundI].heats].sort((a, b) => {
      const aTime = new Date(a.resultIsOfficialTimestamp).getTime();
      const bTime = new Date(b.resultIsOfficialTimestamp).getTime();

      return aTime < bTime ? 1 : aTime > bTime ? -1 : 0;
    });
    for (let heatI = 0; heatI < checkRound.length; heatI++) {
      if (checkRound[heatI].id === heatId) {
        return [checkData[roundI], checkRound[heatI]];
      }
    }
  }

  return [];
};

/**
 * splits heats of a round in branches
 * (2 heats = 1 branch)
 *
 * @param {*} data
 */
const splitBranches = (data, number = 2) => {
  const branches = [];
  let arrIndex = -1;
  for (let i = 0; i < data.length; i++) {
    if (i % number === 0) {
      arrIndex++;

      branches[arrIndex] = [];
    }

    branches[arrIndex].push(data[i]);
  }

  return branches;
};

/**
 * replaces headline if there is a replacement in config
 *
 * @param {string} headline
 */
const prepareHeadline = (headline) => {
  return (
    <AppContext.Consumer>
      {({ config }) => {
        return config && config.replacements && config.replacements[headline]
          ? config.replacements[headline]
          : headline;
      }}
    </AppContext.Consumer>
  );
};

/**
 * Builds the tree view for knockout round
 *
 * @param {{data: *, setHeat: Function, activeHeat: *, category: string}} param0
 */
const Tree = ({ data, setHeat, activeHeat, category, config, focus }) => {
  // sort rounds to be sure they are in correct order
  data.sort(roundSort);

  // get last 4 rounds
  const filteredData = data.slice(-4);
  // receive 5th from end for additional slidein tree
  const additionalRound = data.length > 4 ? data.slice(-5, 1)[0] : undefined;

  const [round, setRound] = useState();
  const [additionalDisabled, setAdditionalDisabled] = useState(true);
  const context = React.useContext(AppContext);
  const {
    finishedText,
    upcomingText,
    partlyFinishedText,
    inprogressText,
    top32,
    top16,
    final,
    quarterFinal,
    semifinal,
    qualifiers
  } = context.lang;

  /**
   * reset view if new category is received
   */
  useEffect(() => {
    // findHeat(dataSet[activeName.toLowerCase()].rounds, {id: focus[focus.length -1].current})
    const activeData = findActive(data, focus ? focus.current : '1');

    if (!activeHeat) {
      setAdditionalDisabled(true);
    }

    if (activeData.length) {
      setRound(activeData[0]);
      setHeat(activeData[1]);
    } else if (filteredData && filteredData[0]) {
      setRound(filteredData[0]);
      if (!activeHeat) {
        setHeat(data.find(({ id }) => focus.current === id));
      }
    }
    // eslint-disable-next-line
  }, [data]);

  const numItems = () => {
    let number = 0;
    number += context.lang.hasOwnProperty('finishedText') ? 1 : 0;
    number += context.lang.hasOwnProperty('partlyFinishedText') ? 1 : 0;
    number += context.lang.hasOwnProperty('upcomingText') ? 1 : 0;
    number += context.lang.hasOwnProperty('inprogressText') ? 1 : 0;

    return number;
  };

  const getHeadline = (name) => {
    switch (name) {
      case 'Top32':
        return getTrans(top32);

      case 'Top16':
        return getTrans(top16);

      case 'Final':
        return getTrans(final);

      case 'Quarterfinals':
        return getTrans(quarterFinal);

      case 'Semifinals':
        return getTrans(semifinal);

      case 'Qualifiers':
        return getTrans(qualifiers);

      default:
        return name;
    }
  };

  return (
    <>
      <div
        className={`knockOutTree2 appRow tree-vsize-${
          filteredData.length > 0 && filteredData[0].heats.length > 5 ? 'high' : 'low'
        }`}
      >
        <div className="knockOutTree2_wrapper">
          {additionalRound && (
            <AdditionalRound
              {...additionalRound}
              setHeat={setHeat}
              activeHeat={activeHeat}
              disabled={additionalDisabled}
              isActiveRound={round && additionalRound.number === round.number}
              config={config}
            />
          )}
          <div className="knockoutTree2_treeWrapper">
            <div className="knockOutTree2_head">
              {filteredData &&
                filteredData.map(({ number, name }, index) => (
                  <div className="round" key={`round-${category}-${number}`}>
                    <h4
                      className={`h4${
                        (round && number === round.number) || (!round && index === 0)
                          ? ' textActive'
                          : ''
                      }`}
                    >
                      {getHeadline(name)}
                    </h4>
                  </div>
                ))}
            </div>
            <div className={`knockOutTree2_tree rounds-${filteredData.length}`}>
              {filteredData &&
                filteredData.map(({ number, name, heats }, index) => {
                  const branches = splitBranches(heats, config.heatsPerBranch || 2);
                  let additionalClasses = [];
                  // if (index === 0 || 1) {
                  //   // first col with more than 5 heats are high trees
                  //   additionalClasses.push(`branches-vsize-${filteredData[0].heats.length > 5 ? 'high' : 'low'}`);
                  // }
                  return (
                    <div className="round" key={`round-${number}`}>
                      <div className={`branches ${additionalClasses.join(' ')}`}>
                        {branches &&
                          branches.map((branch, bindex) => (
                            <Branch
                              key={`branch-${index}${bindex}`}
                              branch={branch}
                              setHeat={setHeat}
                              activeHeat={activeHeat}
                              category={category}
                              config={config}
                            />
                          ))}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
        <div className="knockOutTree2-interactionbar">
          {additionalRound && filteredData && (
            <div
              className={`knockOutTree2_slideLeft`}
              onClick={() => {
                const dataLayerArgs = {
                  // dataLayer: {
                  event: 'rbVideoTracking',
                  event_name: 'video_sidebar',
                  video_sidebar: {
                    value: `${category}::${additionalDisabled ? 'Top64' : 'Finals'}`,
                    widget_type: `Knockout Tree - ${
                      config.trackingTitle || config.navigationTitle
                    }`
                  }
                  // eventAction: 'sidebar',
                  // eventCategory: 'video'
                  // }
                };

                GAtrack(dataLayerArgs);

                setAdditionalDisabled(!additionalDisabled);
              }}
            >
              <>
                <Arrow
                  style={{
                    transform: `${additionalDisabled ? 'rotate(0deg)' : 'rotate(180deg)'}`
                  }}
                />
              </>
              {additionalDisabled ? (
                <>{prepareHeadline(additionalRound.name)}</>
              ) : (
                <>{prepareHeadline('Finals')}</>
              )}
            </div>
          )}
          <ul
            className={`knockOutTree2-legend appRow${
              numItems() > 3 ? '' : ' columnStyle'
            }`}
          >
            {getTrans(finishedText) && (
              <li>
                {getTrans(finishedText)}{' '}
                <span className="knockOutTree2-legend-bubble finished"></span>
              </li>
            )}

            {getTrans(partlyFinishedText) && (
              <li>
                {getTrans(partlyFinishedText)}{' '}
                <span className="knockOutTree2-legend-bubble partly_finished"></span>
              </li>
            )}

            {getTrans(upcomingText) && (
              <li>
                {getTrans(upcomingText)}{' '}
                <span className="knockOutTree2-legend-bubble"></span>
              </li>
            )}

            {getTrans(inprogressText) && (
              <li>
                {getTrans(inprogressText)}{' '}
                <span className="knockOutTree2-legend-bubble inprogress"></span>
              </li>
            )}
          </ul>
        </div>
      </div>
    </>
  );
};

export { Tree, prepareHeadline };
